import React, {useState} from 'react';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useTranslation} from 'react-i18next';

import type {SettingsStackParamList} from '@/app/routes';
import {useAuthStore} from '@/entities/session/auth-store';
import {InviteForm} from '@/features/manage-telegram-recipients/ui/InviteForm';
import {RecipientList} from '@/features/manage-telegram-recipients/ui/RecipientList';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {AppButton} from '@/shared/ui/button';
import {Page} from '@/shared/ui/page';
import {SettingsNav} from '@/pages/settings/SettingsNav';

/**
 * "Организация" → Дашборд → Получатели уведомлений. Two modes on one screen:
 * the list of who is connected, and the form that mints an invite. Minting is a
 * mode rather than a pushed route because the freshly-created link has to be
 * handed over immediately — a route pop would lose it.
 */
type Props = NativeStackScreenProps<SettingsStackParamList, 'Telegram'>;

export function TelegramRecipientsScreen(_: Props) {
  const {t} = useTranslation();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const [creating, setCreating] = useState(false);
  const {isDesktop} = useBreakpoint();

  return (
    <Page
      sideNav={<SettingsNav />}
      // Прокрутка у страницы: список получателей больше не катает себя сам.
      scroll
      testID="company-telegram"
      title={t('telegram.title')}
      /* Открытая форма закрывается раньше самой страницы; когда её нет, стрелку
         ведёт карта экранов — в «Настройки», откуда бы сюда ни пришли. */
      onBack={creating ? () => setCreating(false) : undefined}
      actions={
        companyId && !creating ? (
          <AppButton
            title={t('telegram.create')}
            icon="plus"
            size={isDesktop ? 'md' : 'lg'}
            onPress={() => setCreating(true)}
            testID="telegram-create"
          />
        ) : undefined
      }>
      {companyId ? (
        creating ? (
          <InviteForm companyId={companyId} onDone={() => setCreating(false)} />
        ) : (
          <RecipientList companyId={companyId} onCreate={() => setCreating(true)} />
        )
      ) : null}
    </Page>
  );
}
